import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import VignetteVan from '../components/VignetteVan';
import supabase from '../utils/supabaseClient';
import { Van } from '../types/van';

const itemListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Flotte de vans To The Road",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": {
        "@type": "Product",
        "name": "Le Calif — Volkswagen T5 California",
        "description": "Van aménagé VW T5 California, 4 places, 4 couchages, toit relevable. Au départ de Versailles.",
        "image": "https://totheroad.fr/og-image.jpg",
        "brand": { "@type": "Brand", "name": "Volkswagen" },
        "offers": {
          "@type": "Offer",
          "url": "https://totheroad.fr/reserver",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "seller": { "@type": "Organization", "name": "To The Road" }
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "item": {
        "@type": "Product",
        "name": "Electro-Van — Volkswagen ID. Buzz",
        "description": "Van électrique VW ID. Buzz, 5 places, 2 couchages. Disponible en version van ou tourisme. Au départ de Versailles.",
        "image": "https://totheroad.fr/og-image.jpg",
        "brand": { "@type": "Brand", "name": "Volkswagen" },
        "offers": {
          "@type": "Offer",
          "url": "https://totheroad.fr/reserver",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "seller": { "@type": "Organization", "name": "To The Road" }
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 3,
      "item": {
        "@type": "Product",
        "name": "California T6.1 — Volkswagen T6.1 California",
        "description": "Van aménagé VW T6.1 California, 4 places, 4 couchages. Au départ de Versailles.",
        "image": "https://totheroad.fr/og-image.jpg",
        "brand": { "@type": "Brand", "name": "Volkswagen" },
        "offers": {
          "@type": "Offer",
          "url": "https://totheroad.fr/reserver",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "seller": { "@type": "Organization", "name": "To The Road" }
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 4,
      "item": {
        "@type": "Product",
        "name": "California T6.1 LW — Volkswagen T6.1 California",
        "description": "Van aménagé VW T6.1 California grand format, 4 places, 4 couchages. Au départ de Versailles.",
        "image": "https://totheroad.fr/og-image.jpg",
        "brand": { "@type": "Brand", "name": "Volkswagen" },
        "offers": {
          "@type": "Offer",
          "url": "https://totheroad.fr/reserver",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "seller": { "@type": "Organization", "name": "To The Road" }
        }
      }
    }
  ]
};

const VanBooking: React.FC = () => {
  const [vans, setVans] = useState<Van[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVans = async () => {
      const { data, error } = await supabase
        .from('vans')
        .select('*')
        .eq('visible', true)
        .order('ordre');

      if (error) {
        setError('Impossible de charger les vans. Veuillez réessayer.');
      } else {
        setVans(data ?? []);
      }
      setLoading(false);
    };

    fetchVans();
  }, []);

  return (
    <div className="bg-gradient-to-b from-blue-50 to-blue-100 py-12">
      <Helmet>
        <title>Nos vans à louer — Volkswagen California &amp; ID. Buzz | To The Road</title>
        <meta name="description" content="Découvrez la flotte To The Road : VW T5 California, T6.1 California, ID. Buzz. Vans aménagés au départ de Versailles, réservation sur les plateformes partenaires." />
        <link rel="canonical" href="https://totheroad.fr/reserver" />
        <meta property="og:title" content="Nos vans à louer — Volkswagen California & ID. Buzz | To The Road" />
        <meta property="og:description" content="Découvrez la flotte To The Road : VW T5 California, T6.1 California, ID. Buzz. Vans aménagés au départ de Versailles, réservation sur les plateformes partenaires." />
        <meta property="og:image" content="https://totheroad.fr/og-image.jpg" />
        <meta property="og:url" content="https://totheroad.fr/reserver" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Nos Vans Disponibles
          </h1>
          <p className="text-xl text-gray-600">
            Découvrez notre flotte de vans aménagés pour vos aventures
          </p>
        </div>

        {loading && (
          <div className="text-center text-gray-500 py-12">Chargement...</div>
        )}

        {error && (
          <div className="text-center text-red-500 py-12">{error}</div>
        )}

        <div className="flex flex-col gap-8">
          {vans.map((van) => (
            <VignetteVan
              key={van.id}
              image={van.image_url}
              nom={van.nom}
              modele={van.modele}
              nombreCouchages={van.nombre_couchages}
              nombrePlaces={van.nombre_places}
              motorisation={van.motorisation}
              options={van.options}
              plateformes={[
                { nom: van.nom_plateforme1, lien: van.lien_plateforme1, type: van.type_plateforme1 ?? 'amenage' },
                { nom: van.nom_plateforme2, lien: van.lien_plateforme2, type: van.type_plateforme2 ?? 'amenage' },
                ...(van.nom_plateforme3 ? [{ nom: van.nom_plateforme3, lien: van.lien_plateforme3, type: van.type_plateforme3 ?? 'amenage' }] : []),
                ...(van.nom_plateforme4 ? [{ nom: van.nom_plateforme4, lien: van.lien_plateforme4, type: van.type_plateforme4 ?? 'amenage' }] : []),
              ]}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default VanBooking;
